import React, { useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIdleTimer } from 'react-idle-timer'
import { Box, IconButton } from '@mui/material'
import SearchBar from '../InteractiveSearch/SearchBar'
import FilterDropdown from '../InteractiveSearch/FilterDropdown'
import FavoritesIndicator from './components/FavoritesIndicator'
import LocationSwitcher from './components/LocationSwitcher'
import UserIcon from './components/UserIcon'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LoginButton from './components/LoginButton'
import SignupButton from './components/SignupButton'
import { Container } from 'components'
import * as uiActions from 'modules/UI/actions'
import AuthPopup from 'modules/AuthPopup'
import { AuthPopupEnum } from 'utils/constants'
import { useIsMobile } from 'utils/UI'
import LogoSrc from 'components/Logo/Logo.png'
import LogoSmallSrc from 'components/Logo/LogoSmall.png'
import DropdownMenu from './components/DropdownMenu'
import api from 'utils/api'
import OfferDisplayToggle from 'components/offers/OfferDisplayToggle'
import useOfferDisplayMode from 'components/offers/useOfferDisplayMode'
import { listingType } from 'components/offers/utils'
import {
  applyOfferFilterToSearchParams,
  getOfferFilterFromSearchParams,
} from 'components/InteractiveSearch/offerFilterParams'

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const user = useSelector(state => state.user)
  const showAuthPopup = useSelector(state => state.ui.showAuthPopup)

  const isMobile = useIsMobile()

  const searchParams = new URLSearchParams(location.search)

  const [dropdownMenuAnchor, setDropdownMenuAnchor] = useState(null)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)

  const isSearchPage = location.pathname.startsWith('/search')
  const isLandingPage = location.pathname === '/'

  const [displayMode, setDisplayMode] = useOfferDisplayMode(searchParams.get('display'))
  const displayIsList = displayMode === listingType.list
  const mapActive = searchParams.get('map') === '1'

  const toggleMapInUrl = () => {
    const params = new URLSearchParams(location.search)
    const current = params.get('map') === '1'

    if (current) {
      params.set('map', '0')
    } else {
      params.set('map', '1')
      params.set('list', '1')
    }

    navigate({ pathname: location.pathname, search: params.toString() })
  }

  const changeDisplayMode = (mode) => {
    const params = new URLSearchParams(location.search)
    setDisplayMode(mode)
    params.set('display', mode)
    navigate({ pathname: location.pathname, search: params.toString() })
  }

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget)
  }

  const handleFilterClose = () => {
    setFilterAnchorEl(null)
  }

  const handleFilterSelect = (filter) => {
    const params = applyOfferFilterToSearchParams(location.search, filter)
    navigate({ pathname: location.pathname, search: params.toString() })
  }

  useIdleTimer({
    timeout: 1000 * 60 * 35,
    onActive: () => {
      api.post('/geo', user.location).catch(() => {})
    },
    debounce: 500
  })

  return (
    <header
      style={{
        position: isMobile ? 'static' : 'fixed',
        width: '100%',
        top: 0,
        background: '#fff',
        zIndex: 1000
      }}
    >
      <Container>

        {/* HEADER ROW */}
        <Box
          my={isMobile ? 1 : 3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height={48}
        >

          {/* LOGO */}
          <Box display="flex" alignItems="center">
            <NavLink to="/">
              <img
                src={isMobile ? LogoSmallSrc : LogoSrc}
                height={50}
                alt="HiddenUnderGround logo"
              />
            </NavLink>
          </Box>

          {/* DESKTOP SEARCH */}
          {!isMobile && isSearchPage && (
            <Box flex={1} mx={4} maxWidth={700}>
              <SearchBar
                onMapToggle={toggleMapInUrl}
                onFilterClick={handleFilterClick}
                isMapActive={mapActive}
                isDisplayActive={displayIsList}
                filterAnchorEl={filterAnchorEl}
              />
            </Box>
          )}

          {/* RIGHT SIDE */}
          <Box display="flex" alignItems="center">

            <LocationSwitcher />

            {(isLandingPage || isSearchPage) && (
              <OfferDisplayToggle displayMode={displayMode} onChange={changeDisplayMode} />
            )}

            {user.details ? (
              <>
                {!isLandingPage && !isSearchPage && <FavoritesIndicator />}

                <UserIcon
                  avatar={user.details.imageUrl}
                  name={user.details.firstName || user.details.email}
                  handleClick={(e) => setDropdownMenuAnchor(e.currentTarget)}
                />

                <DropdownMenu
                  anchorElement={dropdownMenuAnchor}
                  onClose={() => setDropdownMenuAnchor(null)}
                />
              </>
            ) : (
              <>
                {!isMobile && (
                  <>
                    <LoginButton />
                    <Box ml={1}>
                      <SignupButton />
                    </Box>
                  </>
                )}

                {isMobile && (
                  <IconButton
                    onClick={() =>
                      dispatch(uiActions.showLogin(AuthPopupEnum.login))
                    }
                  >
                    <AccountCircleIcon />
                  </IconButton>
                )}
              </>
            )}

          </Box>

        </Box>

      </Container>

      {/* MOBILE SEARCH BAR BELOW HEADER */}
      {isMobile && isSearchPage && (
        <Box px={2} pb={2}>
          <SearchBar
            onMapToggle={toggleMapInUrl}
            onFilterClick={handleFilterClick}
            isMapActive={mapActive}
            isDisplayActive={displayIsList}
            filterAnchorEl={filterAnchorEl}
          />
        </Box>
      )}

      <FilterDropdown
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        selectedFilter={getOfferFilterFromSearchParams(searchParams)}
        onFilterSelect={handleFilterSelect}
      />

      {!!showAuthPopup && <AuthPopup />}

    </header>
  )
}

export default Header
